import { useContext, useEffect, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { AuthContext } from '@/context/auth-context'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useAppDispatch } from '@/store/store-hooks'
import { openSettingsModal } from '@/store/slices/ui-state.slice'
import { resolveSettingsModalRedirectAttribution, type SettingsLocationState } from '@/analytics/utils/settings-entry-analytics'
import type { SettingsSectionId } from './nav/settings-nav-sections'

type SettingsModalRedirectRouteProps = {
  /** Legacy page element rendered when the flag is off. */
  children: ReactNode
  /** Section to open when the flag is on. Omit with `resolveByRole` to pick by account type. */
  section?: SettingsSectionId
  /** For the /account route: Subscription (individual) or Members (org admins and members). */
  resolveByRole?: boolean
}

/**
 * Keeps the legacy /dashboard/settings and /dashboard/account deep links working under both flag
 * states. Flag off → renders the existing page. Flag on → opens the settings modal on the right
 * section and redirects to the dashboard so the URL doesn't strand on a now-unused route (and
 * closing the modal reveals a normal page, not the old settings page).
 *
 * The open stays on the original effect schedule. This render's location still has the state
 * and query; the redirect that follows replaces the URL and drops both, except an OAuth return's
 * query, which Connected accounts still needs.
 */
export function SettingsModalRedirectRoute({ children, section, resolveByRole }: SettingsModalRedirectRouteProps) {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { userAccountData } = useContext(AuthContext)
  // Match the account menu: treat the brief loading window as off so flag-off users never flash blank.
  const { enabled, loading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.SETTINGS_REVAMP_FOUNDATION)

  // The /account route (resolveByRole) picks its section from the account role, so it also has to
  // wait for userAccountData before it can decide. The /settings route has an explicit section and
  // only depends on the flag.
  const roleReady = !resolveByRole || !!userAccountData
  const showModal = enabled && !loading && roleReady

  // Org accounts (admins and members) land on the Members roster; Billing is a separate admin-only
  // section reachable from the nav. Individual accounts land on Subscription.
  const roleTarget: SettingsSectionId | undefined = resolveByRole ? (userAccountData?.isOrgAccount ? 'members' : 'subscription') : section

  // An external storage OAuth return carries a single-use handoff that Connected accounts posts to
  // finish the connection. Open that section and keep the query through the redirect so it can.
  const searchParams = new URLSearchParams(location.search)
  const isOAuthReturn = searchParams.has('external_storage_handoff') || searchParams.has('external_storage_error')
  const target: SettingsSectionId | undefined = isOAuthReturn ? 'connected-accounts' : roleTarget

  useEffect(() => {
    if (!showModal) return

    const stateSource = (location.state as SettingsLocationState | null)?.settingsEntrySource
    const attribution = resolveSettingsModalRedirectAttribution({
      isAccountRoute: !!resolveByRole,
      isOrgAccount: !!userAccountData?.isOrgAccount,
      stateSource,
      search: location.search,
    })
    dispatch(
      openSettingsModal({
        section: target,
        settingsEntrySource: attribution.settingsEntrySource,
        ...(attribution.organizationRosterEntrySource ? { organizationRosterEntrySource: attribution.organizationRosterEntrySource } : {}),
      })
    )
    // Location is closed over from the render that decided to open. A later URL replace must not
    // schedule another open: that would reset the section, which the original effect did not do.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- same schedule as the pre-analytics open
  }, [showModal, target, dispatch])

  if (showModal) {
    return <Navigate to={{ pathname: '/dashboard/matters', search: isOAuthReturn ? location.search : '' }} replace />
  }

  // Hold the /account route while its decision is still pending: the flag is loading, or the flag is
  // on but the account role isn't known yet. Rendering children here would let the child guard
  // (AccountPageGuard) redirect and unmount this route before the open effect runs, stranding the
  // modal. A definitive flag-off falls through to the legacy guard, which handles anonymous and the
  // role itself.
  if (resolveByRole && (loading || (enabled && !userAccountData))) {
    return null
  }

  // Hold an OAuth return while the flag loads too. Legacy Settings would post the single-use handoff
  // and strip it from the browser URL, but this route's location keeps the old query, so a flag that
  // then resolves on would redirect with the handoff and the modal would post it a second time.
  if (isOAuthReturn && loading) {
    return null
  }

  return <>{children}</>
}
